const NOTIFY_ME_TRANSITION_DURATION = 300;

class NotifyMeModal extends ModalBase {
  get modalTransition() {
    return {
      overlayOpenClasses: ['opacity-100'],
      overlayClosedClasses: ['opacity-0'],
      panelOpenClasses: ['translate-y-0', 'lg:scale-100', 'lg:opacity-100'],
      panelClosedClasses: ['translate-y-full', 'lg:scale-95', 'lg:opacity-0'],
      transitionDuration: NOTIFY_ME_TRANSITION_DURATION,
    };
  }

  constructor() {
    super();
    this.previousActiveElement = null;
    this.isOpen = false;
    this.focusTrapHandler = null;
    this.unsubscribeVariant = null;
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-notify-me-overlay]');
    this.panel = this.querySelector('[data-notify-me-panel]');
    this.form = this.querySelector('[data-notify-me-form]');
    this.input = this.querySelector('[data-notify-me-input]');
    this.tagsInput = this.querySelector('[data-notify-me-tags]');
    this.submitButton = this.querySelector('[data-notify-me-submit]');
    this.errorEl = this.querySelector('[data-notify-me-error]');
    this.submitErrorEl = this.querySelector('[data-notify-me-submit-error]');

    this.overlay?.addEventListener('click', () => this.close());

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-notify-me-cancel]')) this.close();
    });

    this.form?.addEventListener('submit', (event) => this.onSubmit(event));

    this.handleKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    };
    document.addEventListener('keydown', this.handleKeydown);

    this.handleTriggerClick = (event) => {
      if (event.target.closest('[data-notify-me-trigger]')) {
        event.preventDefault();
        this.open();
      }
    };
    document.addEventListener('click', this.handleTriggerClick);

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
        if (String(data.productId) !== this.dataset.productId || !data.variant?.id || !this.tagsInput) return;
        this.tagsInput.value = `${this.tagsInput.dataset.notifyMeTags}${data.variant.id}`;
      });
    }
  }

  disconnectedCallback() {
    this.unsubscribeVariant?.();
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleTriggerClick);
    this.removeTrapFocus();
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.input) return;

    this.input.value = this.input.value.trim();
    const isValid = this.input.checkValidity();
    this.errorEl?.toggleAttribute('hidden', isValid);
    this.submitErrorEl?.setAttribute('hidden', '');
    if (!isValid) return;

    this.submitButton?.setAttribute('disabled', '');
    try {
      const response = await fetch(this.form.action, { method: 'POST', body: new FormData(this.form) });
      const { pathname, searchParams } = new URL(response.url);

      // Bot check requested: hand over to the native submit so Shopify can show it
      if (pathname.startsWith('/challenge')) {
        this.form.submit();
        return;
      }
      if (!response.ok || searchParams.get('customer_posted') !== 'true') throw new Error(`Notify Me not saved: ${response.status}`);

      this.close();
      document.querySelector('notify-me-success-modal')?.open();
    } catch {
      this.submitErrorEl?.removeAttribute('hidden');
    } finally {
      this.submitButton?.removeAttribute('disabled');
    }
  }

  open() {
    this.openModal();
  }

  close() {
    this.closeModal(() => this.resetForm());
  }

  resetForm() {
    if (this.input) this.input.value = '';
    this.errorEl?.setAttribute('hidden', '');
    this.submitErrorEl?.setAttribute('hidden', '');
  }
}

class NotifyMeSuccessModal extends ModalBase {
  get modalTransition() {
    return {
      overlayOpenClasses: ['opacity-100'],
      overlayClosedClasses: ['opacity-0'],
      panelOpenClasses: ['translate-y-0', 'lg:scale-100', 'lg:opacity-100'],
      panelClosedClasses: ['translate-y-full', 'lg:scale-95', 'lg:opacity-0'],
      transitionDuration: NOTIFY_ME_TRANSITION_DURATION,
    };
  }

  constructor() {
    super();
    this.previousActiveElement = null;
    this.isOpen = false;
    this.focusTrapHandler = null;
    this.unsubscribeVariant = null;
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-notify-me-success-overlay]');
    this.panel = this.querySelector('[data-notify-me-success-panel]');
    this.descriptionEl = this.querySelector('[data-notify-me-success-description]');
    this.cardsEl = this.querySelector('[data-notify-me-success-cards]');

    this.overlay?.addEventListener('click', () => this.close());

    this.addEventListener('click', (event) => {
      if (
        event.target.closest('[data-notify-me-success-cancel]') ||
        event.target.closest('[data-notify-me-success-continue]')
      ) {
        this.close();
      }
    });

    this.handleKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    };
    document.addEventListener('keydown', this.handleKeydown);

    this.initVariantSubscriber();
  }

  disconnectedCallback() {
    this.unsubscribeVariant?.();
    document.removeEventListener('keydown', this.handleKeydown);
    this.removeTrapFocus();
  }

  initVariantSubscriber() {
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
      if (String(data.productId) !== this.dataset.productId) return;
      if (data.variant?.title) this.dataset.variantTitle = data.variant.title;
    });
  }

  open() {
    this.openModal(() => {
      const template = window.theme?.strings?.notifyMeSuccessDescription;
      if (this.descriptionEl && template) {
        this.descriptionEl.textContent = template
          .replace('{{ product }}', this.dataset.productTitle ?? '')
          .replace('{{ variant }}', this.dataset.variantTitle ?? '');
      }
    });
  }

  close() {
    this.closeModal();
  }
}

if (!customElements.get('notify-me-modal')) {
  customElements.define('notify-me-modal', NotifyMeModal);
}

if (!customElements.get('notify-me-success-modal')) {
  customElements.define('notify-me-success-modal', NotifyMeSuccessModal);
}
